import {
    getSiteBuilderBlockDefinition,
    getSiteBuilderLocalizationFields,
    siteBuilderStyleSnapshotFromContext,
    SITE_BUILDER_SCHEMA_VERSION,
    SITE_BUILDER_STYLE_SNAPSHOT_OPTION,
    type SiteBuilderLocalizationFieldDescriptor,
    type SiteBuilderZoneKind,
} from "./siteBuilderSchema";
import {
    createSiteBuilderBlockUuid,
    withSiteBuilderBlockUuids,
} from "./siteBlockIdentity";
import type {
    SiteBuilderBlock,
    SiteBuilderDesign as SiteTemplateDesign,
    SiteBuilderRichTextMark,
    SiteBuilderRichTextSegment,
} from "./siteBuilderTypes";

export {
    createSiteBuilderBlockUuid,
    regenerateSiteBuilderBlockUuids,
    withSiteBuilderBlockUuids,
} from "./siteBlockIdentity";

type SiteTemplateBlock = SiteBuilderBlock;

export const SITE_BUILDER_DOCUMENT_KEY = "builderDocument";

export type SiteBuilderLocalizationResolver = (
    blockType: string,
) => readonly SiteBuilderLocalizationFieldDescriptor[];

export type SiteBuilderDocumentContentJson = {
    blocks?: SiteTemplateBlock[];
    [key: string]: unknown;
};

export type SiteBuilderEditorNode = {
    type?: string;
    text?: string;
    attrs?: Record<string, unknown>;
    marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
    content?: SiteBuilderEditorNode[];
};

export type SiteBuilderEditorDocument = SiteBuilderEditorNode & {
    type: "doc";
    content?: SiteBuilderEditorNode[];
};

export type SiteBuilderZoneDocument = {
    accepts?: string[];
    blocks?: SiteTemplateBlock[];
    capacity?: number;
    editor?: SiteBuilderEditorDocument;
    extendedRows?: boolean;
    id: string;
    key: string;
    kind: SiteBuilderZoneKind;
    textFlow?: boolean;
};

export type SiteBuilderSectionDocument = {
    block: SiteTemplateBlock;
    id: string;
    presetKey: string;
    zones: SiteBuilderZoneDocument[];
};

export type SiteBuilderDocument = {
    schemaVersion: number;
    sections: SiteBuilderSectionDocument[];
};

function cloneBlock(block: SiteTemplateBlock): SiteTemplateBlock {
    return JSON.parse(JSON.stringify(block)) as SiteTemplateBlock;
}

function cloneBlocks(blocks: SiteTemplateBlock[]): SiteTemplateBlock[] {
    return blocks.map(cloneBlock);
}

function plainTextFromValue(value: unknown): string {
    return typeof value === "string" ? value : "";
}

// Keep the legacy marker on the wire so existing Shop documents remain readable.
const SITE_BUILDER_RICH_TEXT_MARK_TYPE = "shopRichText";
const siteBuilderRichTextMarkKeys = [
    "fontFamily",
    "fontSize",
    "fontStyle",
    "fontWeight",
    "lineHeight",
    "textAlign",
    "textColor",
    "textDecoration",
] as const satisfies readonly (keyof SiteBuilderRichTextMark)[];

function normalizedRichTextMarks(value: unknown): SiteBuilderRichTextMark | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    const marks: SiteBuilderRichTextMark = {};

    for (const key of siteBuilderRichTextMarkKeys) {
        const markValue = value[key];

        if (typeof markValue === "string" && markValue !== "") {
            marks[key] = markValue;
        }
    }

    return Object.keys(marks).length > 0 ? marks : undefined;
}

function normalizedRichTextSegments(value: unknown): SiteBuilderRichTextSegment[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((candidate): SiteBuilderRichTextSegment[] => {
        if (!isRecord(candidate) || typeof candidate.text !== "string") {
            return [];
        }

        const marks = normalizedRichTextMarks(candidate.marks);

        return [{
            text: candidate.text,
            ...(marks ? { marks } : {}),
        }];
    });
}

function richTextPlainText(segments: readonly SiteBuilderRichTextSegment[]): string {
    return segments.map((segment) => segment.text).join("");
}

function textNode(text: string, marks?: SiteBuilderRichTextMark): SiteBuilderEditorNode {
    return {
        type: "text",
        text,
        ...(marks ? {
            marks: [{
                type: SITE_BUILDER_RICH_TEXT_MARK_TYPE,
                attrs: { ...marks },
            }],
        } : {}),
    };
}

function hardBreakNode(marks?: SiteBuilderRichTextMark): SiteBuilderEditorNode {
    return {
        type: "hardBreak",
        ...(marks ? {
            marks: [{
                type: SITE_BUILDER_RICH_TEXT_MARK_TYPE,
                attrs: { ...marks },
            }],
        } : {}),
    };
}

function editorNodesFromText(text: string, marks?: SiteBuilderRichTextMark): SiteBuilderEditorNode[] {
    return text.split("\n").flatMap((part, index, parts): SiteBuilderEditorNode[] => [
        ...(part !== "" ? [textNode(part, marks)] : []),
        ...(index < parts.length - 1 ? [hardBreakNode(marks)] : []),
    ]);
}

function editorDocumentFromRichText(segments: readonly SiteBuilderRichTextSegment[]): SiteBuilderEditorDocument {
    return {
        type: "doc",
        content: [{
            type: "paragraph",
            content: segments
                .filter((segment) => segment.text !== "")
                .flatMap((segment) => editorNodesFromText(
                    segment.text,
                    normalizedRichTextMarks(segment.marks),
                )),
        }],
    };
}

export function siteBuilderEmptyEditorDocument(): SiteBuilderEditorDocument {
    return {
        type: "doc",
        content: [{
            type: "paragraph",
            content: [],
        }],
    };
}

export function siteBuilderEditorDocumentFromText(text: string): SiteBuilderEditorDocument {
    return {
        type: "doc",
        content: [{
            type: "paragraph",
            content: text.trim() === "" && !text.includes("\n") ? [] : editorNodesFromText(text),
        }],
    };
}

export function siteBuilderPlainTextFromEditorNode(node: SiteBuilderEditorNode | null | undefined): string {
    if (!node) {
        return "";
    }

    if (node.type === "hardBreak") {
        return "\n";
    }

    const ownText = typeof node.text === "string" ? node.text : "";
    const childText = Array.isArray(node.content)
        ? node.content.map((child) => siteBuilderPlainTextFromEditorNode(child)).join("")
        : "";

    return `${ownText}${childText}`;
}

function contentKeyFromFieldCode(fieldCode: unknown, fallback: string): string {
    if (typeof fieldCode !== "string" || !fieldCode.startsWith("content.")) {
        return fallback;
    }

    const key = fieldCode.slice("content.".length);

    return key !== "" && !key.includes(".") ? key : fallback;
}

function primaryTextField(
    block: SiteTemplateBlock,
    localizationResolver: SiteBuilderLocalizationResolver,
): {
    fieldCode: string;
    richText?: SiteBuilderRichTextSegment[];
    text: string;
} | null {
    const fields = localizationResolver(block.type)
        .filter((field) => field.localizable && field.fieldCode.startsWith("content."));

    for (const field of fields) {
        const key = contentKeyFromFieldCode(field.fieldCode, "text");
        const value = plainTextFromValue(block.content?.[key]);

        const richText = field.format === "richText"
            ? normalizedRichTextSegments(block.richText?.[key])
            : [];
        const richTextValue = richTextPlainText(richText);

        if (value !== "") {
            return {
                fieldCode: field.fieldCode,
                text: value,
                ...(richText.length > 0 && richTextValue === value ? { richText } : {}),
            };
        }

        if (richText.length > 0 && richTextValue !== "") {
            return { fieldCode: field.fieldCode, richText, text: richTextValue };
        }
    }

    return null;
}

function defaultFieldCodeForBlock(
    blockType: string,
    localizationResolver: SiteBuilderLocalizationResolver,
): string {
    return localizationResolver(blockType)
        .find((field) => field.localizable && field.fieldCode.startsWith("content."))
        ?.fieldCode ?? "content.text";
}

function editorNodeTypeForBlock(blockType: string): string {
    if (blockType === "heading") {
        return "heading";
    }

    if (blockType === "quote" || blockType === "blockquote") {
        return "blockquote";
    }

    if (blockType === "code") {
        return "codeBlock";
    }

    return "paragraph";
}

function tableCellEditorNode(text: string, type: "tableCell" | "tableHeader"): SiteBuilderEditorNode {
    return {
        type,
        content: [{
            type: "paragraph",
            content: editorNodesFromText(text),
        }],
    };
}

function tableEditorDocument(block: SiteTemplateBlock): SiteBuilderEditorDocument {
    const items = plainTextFromValue(block.content?.items);
    const rows = items === "" ? [] : items.split("\n");

    return {
        type: "doc",
        content: [{
            type: "table",
            attrs: {
                blockUuid: block.uuid,
                blockType: block.type,
                fieldCode: "content.items",
            },
            content: rows.map((row, rowIndex) => ({
                type: "tableRow",
                content: row.split("|").map((cell) => tableCellEditorNode(
                    cell,
                    rowIndex === 0 ? "tableHeader" : "tableCell",
                )),
            })),
        }],
    };
}

function blockEditorDocument(
    block: SiteTemplateBlock,
    localizationResolver: SiteBuilderLocalizationResolver,
): SiteBuilderEditorDocument {
    if (block.type === "table") {
        return tableEditorDocument(block);
    }

    const primaryText = primaryTextField(block, localizationResolver);

    if (primaryText) {
        const document = primaryText.richText
            ? editorDocumentFromRichText(primaryText.richText)
            : siteBuilderEditorDocumentFromText(primaryText.text);
        const firstNode = document.content?.[0];

        if (firstNode) {
            firstNode.type = editorNodeTypeForBlock(block.type);
            firstNode.attrs = {
                ...(firstNode.attrs ?? {}),
                blockUuid: block.uuid,
                blockType: block.type,
                fieldCode: primaryText.fieldCode,
                ...(primaryText.richText ? { shopRichText: true } : {}),
                ...(block.type === "heading" && typeof block.options?.level === "number"
                    ? { level: block.options.level }
                    : {}),
            };
        }

        return document;
    }

    return {
        type: "doc",
        content: [{
            type: "builderBlock",
            attrs: { block: cloneBlock(block) },
        }, {
            type: "paragraph",
            content: [],
        }],
    };
}

function zoneKindForSlot(blockType: string, slotKey: string): SiteBuilderZoneKind {
    if (slotKey === "grid") {
        return "grid";
    }

    const definition = getSiteBuilderBlockDefinition(blockType);
    const zone = definition.zones?.find((item) => item.key === slotKey);

    return zone?.kind ?? "slot";
}

function zonesForBlock(
    block: SiteTemplateBlock,
    localizationResolver: SiteBuilderLocalizationResolver,
): SiteBuilderZoneDocument[] {
    const slots = block.slots && typeof block.slots === "object" && !Array.isArray(block.slots)
        ? block.slots
        : {};
    const slotZones = Object.entries(slots).map(([slotKey, slotBlocks]) => ({
        blocks: Array.isArray(slotBlocks) ? cloneBlocks(slotBlocks) : [],
        id: `${block.uuid}:slot:${slotKey}`,
        key: slotKey,
        kind: zoneKindForSlot(block.type, slotKey),
        textFlow: true,
    } satisfies SiteBuilderZoneDocument));

    if (slotZones.length > 0) {
        return slotZones;
    }

    const definition = getSiteBuilderBlockDefinition(block.type);
    const usesEditor = definition.textFlow || block.type === "table";

    return [{
        ...(usesEditor ? { editor: blockEditorDocument(block, localizationResolver) } : { blocks: [] }),
        id: usesEditor ? `${block.uuid}:content` : `${block.uuid}:slot:content`,
        key: "content",
        kind: usesEditor ? "document" : "slot",
        textFlow: true,
    }];
}

export function siteBuilderDocumentFromLegacyBlocks(
    blocks: SiteTemplateBlock[],
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteBuilderDocument {
    return {
        schemaVersion: SITE_BUILDER_SCHEMA_VERSION,
        sections: blocks.map((legacyBlock) => {
            const block = withSiteBuilderBlockUuids(legacyBlock);
            const presetKey = typeof block.options?.presetKey === "string" ? block.options.presetKey : block.type;

            return {
                block: cloneBlock(block),
                id: block.uuid ?? createSiteBuilderBlockUuid(),
                presetKey,
                zones: zonesForBlock(block, localizationResolver),
            };
        }),
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isZoneDocument(value: unknown): value is SiteBuilderZoneDocument {
    return isRecord(value) && typeof value.id === "string" && typeof value.key === "string" && typeof value.kind === "string";
}

function isSectionDocument(value: unknown): value is SiteBuilderSectionDocument {
    return isRecord(value)
        && typeof value.id === "string"
        && typeof value.presetKey === "string"
        && isRecord(value.block)
        && typeof value.block.type === "string"
        && Array.isArray(value.zones)
        && value.zones.every(isZoneDocument);
}

export function isSiteBuilderDocument(value: unknown): value is SiteBuilderDocument {
    return isRecord(value)
        && Number.isFinite(Number(value.schemaVersion))
        && Array.isArray(value.sections)
        && value.sections.every(isSectionDocument);
}

function fallbackBlockTypeForEditorNode(nodeType: string | undefined): string {
    if (nodeType === "heading") {
        return "heading";
    }

    if (nodeType === "blockquote") {
        return "blockquote";
    }

    if (nodeType === "codeBlock") {
        return "code";
    }

    if (nodeType === "bulletList" || nodeType === "orderedList") {
        return "list";
    }

    if (nodeType === "taskList") {
        return "checklist";
    }

    if (nodeType === "image") {
        return "image";
    }

    if (nodeType === "table") {
        return "table";
    }

    if (nodeType === "horizontalRule") {
        return "separator";
    }

    return "paragraph";
}

function withEditorNodeIdentity(
    node: SiteBuilderEditorNode,
    rootBlock: SiteTemplateBlock,
    nodeIndex: number,
    localizationResolver: SiteBuilderLocalizationResolver,
): SiteBuilderEditorNode {
    if (node.type === "builderBlock") {
        const embeddedBlock = node.attrs?.block;

        if (!isRecord(embeddedBlock) || typeof embeddedBlock.type !== "string") {
            return node;
        }

        return {
            ...node,
            attrs: {
                ...(node.attrs ?? {}),
                block: withSiteBuilderBlockUuids(embeddedBlock as SiteTemplateBlock),
            },
        };
    }

    const blockType = typeof node.attrs?.blockType === "string" && node.attrs.blockType !== ""
        ? node.attrs.blockType
        : nodeIndex === 0
            ? rootBlock.type
            : fallbackBlockTypeForEditorNode(node.type);
    const blockUuid = typeof node.attrs?.blockUuid === "string" && node.attrs.blockUuid !== ""
        ? node.attrs.blockUuid
        : nodeIndex === 0
            ? rootBlock.uuid
            : createSiteBuilderBlockUuid();
    const fieldCode = typeof node.attrs?.fieldCode === "string" && node.attrs.fieldCode !== ""
        ? node.attrs.fieldCode
        : defaultFieldCodeForBlock(blockType, localizationResolver);

    return {
        ...node,
        attrs: {
            ...(node.attrs ?? {}),
            blockType,
            blockUuid,
            fieldCode,
        },
    };
}

function withEditorDocumentIdentities(
    document: SiteBuilderEditorDocument,
    rootBlock: SiteTemplateBlock,
    localizationResolver: SiteBuilderLocalizationResolver,
): SiteBuilderEditorDocument {
    return {
        ...document,
        content: document.content?.map((node, index) => withEditorNodeIdentity(
            node,
            rootBlock,
            index,
            localizationResolver,
        )),
    };
}

export function withSiteBuilderDocumentIdentities(
    document: SiteBuilderDocument,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteBuilderDocument {
    return {
        ...document,
        sections: document.sections.map((section) => {
            const block = withSiteBuilderBlockUuids(section.block);

            return {
                ...section,
                block,
                id: block.uuid ?? createSiteBuilderBlockUuid(),
                zones: section.zones.map((zone) => ({
                    ...zone,
                    ...(zone.blocks ? { blocks: zone.blocks.map(withSiteBuilderBlockUuids) } : {}),
                    ...(zone.editor ? {
                        editor: withEditorDocumentIdentities(zone.editor, block, localizationResolver),
                    } : {}),
                    id: zone.editor
                        ? `${block.uuid}:content`
                        : `${block.uuid}:slot:${zone.key}`,
                })),
            };
        }),
    };
}

function blockFromBuilderBlockNode(node: SiteBuilderEditorNode): SiteTemplateBlock | null {
    const block = node.attrs?.block;

    return isRecord(block) && typeof block.type === "string" ? cloneBlock(block as SiteTemplateBlock) : null;
}

function appendTextDecoration(current: string | undefined, decoration: string): string {
    const values = new Set((current ?? "").split(/\s+/).filter((value) => value !== ""));

    values.add(decoration);

    return Array.from(values).join(" ");
}

function richTextMarksFromEditorMarks(
    editorMarks: SiteBuilderEditorNode["marks"],
): SiteBuilderRichTextMark | undefined {
    if (!Array.isArray(editorMarks)) {
        return undefined;
    }

    const marks: SiteBuilderRichTextMark = {};

    for (const editorMark of editorMarks) {
        if (!editorMark || typeof editorMark.type !== "string") {
            continue;
        }

        if (editorMark.type === SITE_BUILDER_RICH_TEXT_MARK_TYPE) {
            Object.assign(marks, normalizedRichTextMarks(editorMark.attrs));
            continue;
        }

        if (editorMark.type === "bold" || editorMark.type === "strong") {
            marks.fontWeight = "700";
            continue;
        }

        if (editorMark.type === "italic" || editorMark.type === "em") {
            marks.fontStyle = "italic";
            continue;
        }

        if (editorMark.type === "underline") {
            marks.textDecoration = appendTextDecoration(marks.textDecoration, "underline");
            continue;
        }

        if (editorMark.type === "strike") {
            marks.textDecoration = appendTextDecoration(marks.textDecoration, "line-through");
            continue;
        }

        if (editorMark.type === "textStyle") {
            Object.assign(marks, normalizedRichTextMarks({
                ...(editorMark.attrs ?? {}),
                textColor: editorMark.attrs?.textColor ?? editorMark.attrs?.color,
            }));
        }
    }

    return Object.keys(marks).length > 0 ? marks : undefined;
}

function richTextSegmentsFromEditorNode(node: SiteBuilderEditorNode): SiteBuilderRichTextSegment[] {
    if (node.type === "hardBreak") {
        const marks = richTextMarksFromEditorMarks(node.marks);

        return [{
            text: "\n",
            ...(marks ? { marks } : {}),
        }];
    }

    const ownSegments: SiteBuilderRichTextSegment[] = [];

    if (typeof node.text === "string" && node.text !== "") {
        const marks = richTextMarksFromEditorMarks(node.marks);

        ownSegments.push({
            text: node.text,
            ...(marks ? { marks } : {}),
        });
    }

    const childSegments = Array.isArray(node.content)
        ? node.content.flatMap(richTextSegmentsFromEditorNode)
        : [];

    return [...ownSegments, ...childSegments];
}

function compactRichTextSegments(segments: readonly SiteBuilderRichTextSegment[]): SiteBuilderRichTextSegment[] {
    return segments.reduce<SiteBuilderRichTextSegment[]>((result, segment) => {
        if (segment.text === "") {
            return result;
        }

        const previous = result.at(-1);

        if (previous && JSON.stringify(previous.marks ?? {}) === JSON.stringify(segment.marks ?? {})) {
            previous.text += segment.text;
            return result;
        }

        result.push({
            text: segment.text,
            ...(segment.marks ? { marks: { ...segment.marks } } : {}),
        });

        return result;
    }, []);
}

function slicedRichTextSegments(
    segments: readonly SiteBuilderRichTextSegment[],
    start: number,
    end: number,
): SiteBuilderRichTextSegment[] {
    let offset = 0;

    return segments.flatMap((segment): SiteBuilderRichTextSegment[] => {
        const segmentStart = offset;
        const segmentEnd = offset + segment.text.length;
        const sliceStart = Math.max(start, segmentStart);
        const sliceEnd = Math.min(end, segmentEnd);

        offset = segmentEnd;

        if (sliceStart >= sliceEnd) {
            return [];
        }

        return [{
            text: segment.text.slice(sliceStart - segmentStart, sliceEnd - segmentStart),
            ...(segment.marks ? { marks: segment.marks } : {}),
        }];
    });
}

function editorTextBlock(
    type: string,
    text: string,
    attrs?: Record<string, unknown>,
    editorSegments: SiteBuilderRichTextSegment[] = [],
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteTemplateBlock | null {
    const fallbackBlockType = type === "heading"
        ? "heading"
        : type === "blockquote"
            ? "blockquote"
            : type === "codeBlock"
                ? "code"
                : "paragraph";
    const blockType = typeof attrs?.blockType === "string" && attrs.blockType !== ""
        ? attrs.blockType
        : fallbackBlockType;
    const fallbackContentKey = blockType === "heading"
        ? "title"
        : blockType === "code"
            ? "code"
            : "text";
    const contentKey = contentKeyFromFieldCode(attrs?.fieldCode, fallbackContentKey);
    const descriptor = localizationResolver(blockType)
        .find((field) => field.fieldCode === `content.${contentKey}`);
    const preservesBoundaryLineBreak = type !== "codeBlock"
        && descriptor?.multiline === true
        && (text.startsWith("\n") || text.endsWith("\n"));
    const leadingTrim = type === "codeBlock"
        ? 0
        : preservesBoundaryLineBreak
            ? text.length - text.replace(/^[\t\f\v ]+/, "").length
            : text.length - text.trimStart().length;
    const trailingTrim = type === "codeBlock"
        ? 0
        : preservesBoundaryLineBreak
            ? text.length - text.replace(/[\t\f\v ]+$/, "").length
            : text.length - text.trimEnd().length;
    const contentValue = text.slice(leadingTrim, text.length - trailingTrim);

    if (contentValue.trim() === "") {
        return null;
    }

    const richTextSource = richTextPlainText(editorSegments);
    const richText = compactRichTextSegments(slicedRichTextSegments(
        editorSegments,
        leadingTrim,
        richTextSource.length - trailingTrim,
    ));
    const shouldPreserveRichText = descriptor?.format === "richText"
        && richText.length > 0
        && richTextPlainText(richText) === contentValue
        && (attrs?.shopRichText === true || richText.some((segment) => segment.marks));

    return {
        uuid: typeof attrs?.blockUuid === "string" && attrs.blockUuid !== ""
            ? attrs.blockUuid
            : createSiteBuilderBlockUuid(),
        type: blockType,
        content: { [contentKey]: contentValue },
        ...(shouldPreserveRichText ? { richText: { [contentKey]: richText } } : {}),
        ...(blockType === "heading" && typeof attrs?.level === "number"
            ? { options: { level: attrs.level } }
            : {}),
    };
}

function listItems(node: SiteBuilderEditorNode): string[] {
    return (node.content ?? []).flatMap((item) => {
        const text = siteBuilderPlainTextFromEditorNode(item).trim();

        return text === "" ? [] : [text];
    });
}

function tableItems(node: SiteBuilderEditorNode): string {
    return (node.content ?? [])
        .filter((row) => row.type === "tableRow")
        .map((row) => (row.content ?? [])
            .filter((cell) => cell.type === "tableCell" || cell.type === "tableHeader")
            .map((cell) => siteBuilderPlainTextFromEditorNode(cell).replace(/\s*\n\s*/g, " ").trim())
            .join("|"))
        .join("\n");
}

export function siteBuilderBlocksFromEditorDocument(
    document: SiteBuilderEditorDocument | null | undefined,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteTemplateBlock[] {
    if (!document?.content) {
        return [];
    }

    return document.content.flatMap((node): SiteTemplateBlock[] => {
        if (node.type === "builderBlock") {
            const block = blockFromBuilderBlockNode(node);

            return block ? [block] : [];
        }

        if (node.type === "horizontalRule") {
            return [{
                uuid: typeof node.attrs?.blockUuid === "string" ? node.attrs.blockUuid : createSiteBuilderBlockUuid(),
                type: typeof node.attrs?.blockType === "string" ? node.attrs.blockType : "separator",
            }];
        }

        if (node.type === "image") {
            return [{
                uuid: typeof node.attrs?.blockUuid === "string" ? node.attrs.blockUuid : createSiteBuilderBlockUuid(),
                type: typeof node.attrs?.blockType === "string" ? node.attrs.blockType : "image",
                content: {
                    alt: plainTextFromValue(node.attrs?.alt),
                    imageUrl: plainTextFromValue(node.attrs?.src),
                    title: plainTextFromValue(node.attrs?.title),
                },
            }];
        }

        if (node.type === "bulletList" || node.type === "orderedList" || node.type === "taskList") {
            const items = listItems(node);

            return items.length > 0 ? [{
                uuid: typeof node.attrs?.blockUuid === "string" ? node.attrs.blockUuid : createSiteBuilderBlockUuid(),
                type: typeof node.attrs?.blockType === "string" ? node.attrs.blockType : "list",
                content: {
                    items: items.join("\n"),
                    style: node.type,
                },
            }] : [];
        }

        if (node.type === "table") {
            return [{
                uuid: typeof node.attrs?.blockUuid === "string" ? node.attrs.blockUuid : createSiteBuilderBlockUuid(),
                type: typeof node.attrs?.blockType === "string" ? node.attrs.blockType : "table",
                content: { items: tableItems(node) },
            }];
        }

        const text = siteBuilderPlainTextFromEditorNode(node);
        const block = editorTextBlock(
            node.type ?? "paragraph",
            text,
            node.attrs,
            richTextSegmentsFromEditorNode(node),
            localizationResolver,
        );

        return block ? [block] : [];
    });
}

function materializeBlock(block: SiteTemplateBlock, design?: SiteTemplateDesign | null): SiteTemplateBlock {
    if (block.options?.[SITE_BUILDER_STYLE_SNAPSHOT_OPTION] || !design) {
        return cloneBlock(block);
    }

    return {
        ...cloneBlock(block),
        options: {
            ...(block.options ?? {}),
            [SITE_BUILDER_STYLE_SNAPSHOT_OPTION]: siteBuilderStyleSnapshotFromContext(design),
        },
    };
}

function legacyBlockFromSection(
    section: SiteBuilderSectionDocument,
    design: SiteTemplateDesign | null | undefined,
    localizationResolver: SiteBuilderLocalizationResolver,
): SiteTemplateBlock[] {
    let block = materializeBlock(section.block, design);
    const nextSlots: Record<string, SiteTemplateBlock[]> = {};
    const editorBlocks: SiteTemplateBlock[] = [];

    for (const zone of section.zones) {
        let zoneBlocks = [
            ...(zone.editor ? siteBuilderBlocksFromEditorDocument(zone.editor, localizationResolver) : []),
            ...(zone.blocks ?? []),
        ].map((item) => {
            const materialized = materializeBlock(item, design);

            if (!block.uuid || materialized.uuid !== block.uuid) {
                return materialized;
            }

            return {
                ...block,
                ...materialized,
                ...(block.content || materialized.content ? {
                    content: {
                        ...(block.content ?? {}),
                        ...(materialized.content ?? {}),
                    },
                } : {}),
                ...(block.options || materialized.options ? {
                    options: {
                        ...(block.options ?? {}),
                        ...(materialized.options ?? {}),
                    },
                } : {}),
                ...(block.richText || materialized.richText ? {
                    richText: {
                        ...(block.richText ?? {}),
                        ...(materialized.richText ?? {}),
                    },
                } : {}),
            };
        });

        if (zone.editor && !getSiteBuilderBlockDefinition(block.type).textFlow) {
            const rootEditorBlock = zoneBlocks.find((item) => item.uuid === block.uuid);

            if (rootEditorBlock) {
                block = rootEditorBlock;
                zoneBlocks = zoneBlocks.filter((item) => item !== rootEditorBlock);
            }
        }

        if (zone.key === "content" && getSiteBuilderBlockDefinition(block.type).textFlow && zoneBlocks.length > 0) {
            editorBlocks.push(...zoneBlocks);
            continue;
        }

        if (zoneBlocks.length > 0 || zone.kind === "extendedSlot" || zone.kind === "grid") {
            nextSlots[zone.key] = zoneBlocks;
        }
    }

    if (editorBlocks.length > 0) {
        return editorBlocks;
    }

    return [{
        ...block,
        ...(Object.keys(nextSlots).length > 0 ? { slots: nextSlots } : {}),
    }];
}

export function siteBuilderLegacyBlocksFromDocument(
    document: SiteBuilderDocument,
    design?: SiteTemplateDesign | null,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteTemplateBlock[] {
    return withSiteBuilderDocumentIdentities(document, localizationResolver).sections
        .flatMap((section) => legacyBlockFromSection(section, design, localizationResolver));
}

export function siteBuilderDocumentFromContentJson(
    value: unknown,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): SiteBuilderDocument {
    if (isRecord(value) && isSiteBuilderDocument(value[SITE_BUILDER_DOCUMENT_KEY])) {
        return withSiteBuilderDocumentIdentities(value[SITE_BUILDER_DOCUMENT_KEY], localizationResolver);
    }

    const blocks = isRecord(value) && Array.isArray(value.blocks)
        ? value.blocks.filter((block): block is SiteTemplateBlock => isRecord(block) && typeof block.type === "string")
        : [];

    return siteBuilderDocumentFromLegacyBlocks(blocks, localizationResolver);
}

export function siteBuilderContentJsonFromDocument(
    document: SiteBuilderDocument,
    extras: Record<string, unknown>,
    design?: SiteTemplateDesign | null,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): { blocks: SiteTemplateBlock[]; [key: string]: unknown } {
    const normalizedDocument = withSiteBuilderDocumentIdentities(document, localizationResolver);

    return {
        ...extras,
        [SITE_BUILDER_DOCUMENT_KEY]: normalizedDocument,
        siteBuilderSchemaVersion: SITE_BUILDER_SCHEMA_VERSION,
        blocks: siteBuilderLegacyBlocksFromDocument(normalizedDocument, design, localizationResolver),
    };
}

export function siteBuilderZoneOverflow(
    zone: SiteBuilderZoneDocument,
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
): number {
    const capacity = typeof zone.capacity === "number" ? zone.capacity : null;

    if (capacity === null) {
        return 0;
    }

    const count = (zone.blocks?.length ?? 0)
        + (zone.editor ? siteBuilderBlocksFromEditorDocument(zone.editor, localizationResolver).length : 0);

    return Math.max(0, count - capacity);
}

export function createSiteBuilderDocumentCodec(
    localizationResolver: SiteBuilderLocalizationResolver = getSiteBuilderLocalizationFields,
) {
    return {
        blocksFromEditorDocument: (
            document: SiteBuilderEditorDocument | null | undefined,
        ) => siteBuilderBlocksFromEditorDocument(document, localizationResolver),
        contentJsonFromDocument: (
            document: SiteBuilderDocument,
            extras: Record<string, unknown>,
            design?: SiteTemplateDesign | null,
        ) => siteBuilderContentJsonFromDocument(
            document,
            extras,
            design,
            localizationResolver,
        ),
        documentFromContentJson: (
            value: unknown,
        ) => siteBuilderDocumentFromContentJson(value, localizationResolver),
        documentFromLegacyBlocks: (
            blocks: SiteTemplateBlock[],
        ) => siteBuilderDocumentFromLegacyBlocks(blocks, localizationResolver),
        legacyBlocksFromDocument: (
            document: SiteBuilderDocument,
            design?: SiteTemplateDesign | null,
        ) => siteBuilderLegacyBlocksFromDocument(
            document,
            design,
            localizationResolver,
        ),
        withDocumentIdentities: (
            document: SiteBuilderDocument,
        ) => withSiteBuilderDocumentIdentities(document, localizationResolver),
        zoneOverflow: (
            zone: SiteBuilderZoneDocument,
        ) => siteBuilderZoneOverflow(zone, localizationResolver),
    };
}

export type SiteBuilderDocumentCodec = ReturnType<typeof createSiteBuilderDocumentCodec>;
